import React from 'react';

import { Formik } from 'formik';
import * as Yup from 'yup';

import { CONFIGS } from '../../utilities/Configs';
import { ConstructFetchBody, UrlQueryParam } from '../../utilities/Helpers';
import { Currencies } from '../../utilities/Lists';
import { LocationPartContainer, LocationPartGroup } from '../../utilities/Types';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField } from '@material-ui/core';

type Props = {
	container: LocationPartContainer,
	partGroups: Array<LocationPartGroup>,
	onAdd: () => void,
	onClose: () => void
};

export const PartAdd = (props: Props) => {
	const handleSubmit = (_values, _formikBag) => {
		const post = [
			-1,
			props.container['type'],
			props.container['id'],
			(parseInt(_values.partGroupInventoryItem) || -1),
			_values.name,
			_values.partNumber,
			_values.serialNumber,
			(parseInt(_values.cost) || 0),
			(parseInt(_values.currency) || -1),
			(_values.dateInstalled || '0001-01-01'),
			(_values.dateServiced || '0001-01-01'),
			(parseInt(_values.serviceInterval) || 0),
			(parseInt(_values.sortOrder) || 0),
			_values.active
		];
		
		fetch(CONFIGS.API.ENDPOINT, ConstructFetchBody(CONFIGS.API.METHODS.PART_UPSERT, [...[UrlQueryParam('token')], ...post])).then(
            (_response) => _response.json()
        ).then(
            (_response) => {
				if (_response['error'] !== undefined) {
					alert('Error: ' + _response['error']['message']);
				}
				else {
					props.onAdd();
					props.onClose();
				}
			}
        ).catch(
            () => alert('An unexpected error has occurred. Please try again later. If problem persists, please contact support.')
        ).finally(
			() => _formikBag.setSubmitting(false)
		);
	};
	
	return (
		<Formik
			initialValues={{
				partGroup: '',
				partGroupInventoryItem: '',
				name: '',
				partNumber: '',
				serialNumber: '',
				cost: '',
				currency: '',
				dateInstalled: '',
				dateServiced: '',
				serviceInterval: '',
				sortOrder: '',
				active: 1
			}}
			onSubmit={handleSubmit}
			validateOnBlur={false}
			validateOnChange={false}
			validationSchema={
				Yup.object().shape({
					partGroup: Yup.number(),
					partGroupInventoryItem: Yup.number(),
					name: Yup.string(),
					partNumber: Yup.string(),
					serialNumber: Yup.string(),
					cost: Yup.number().min(0, 'Value lower than 0'),
					currency: Yup.number(),
					dateInstalled: Yup.string(),
					dateServiced: Yup.string(),
					serviceInterval: Yup.number(),
					sortOrder: Yup.number().min(0, 'Value lower than 0'),
					active: Yup.number()
				})
			}
		>
			{
				(_formik) => (
					<Dialog open maxWidth="sm" fullWidth>
						<DialogTitle>Add Equipment</DialogTitle>
						<DialogContent dividers>
							<Grid container spacing={1}>
								<Grid item xs={12}>
									<TextField
										name="partGroup"
										value={_formik.values.partGroup}
										label="Part Groups"
										select
										InputLabelProps={{
											shrink: true
										}}
										variant="outlined"
										margin="dense"
										fullWidth
										onChange={
											(_event) => {
												_formik.setFieldValue('partGroup', _event.target.value);
												_formik.setFieldValue('partGroupInventoryItem', '');
											}
										}
									>
										<MenuItem value="" disabled>Select...</MenuItem>
										{
											props.partGroups.map(
												(_partGroup, _i) => (
													<MenuItem key={_i} value={_partGroup['Parts Group ID']}>{_partGroup['Parts Group Name']}</MenuItem>
												)
											)
										}
									</TextField>
								</Grid>
								<Grid item xs={12}>
									<TextField
										name="partGroupInventoryItem"
										value={_formik.values.partGroupInventoryItem}
										label="Parts Inventory"
										select
										InputLabelProps={{
											shrink: true
										}}
										variant="outlined"
										margin="dense"
										fullWidth
										onChange={(_event) => _formik.setFieldValue('partGroupInventoryItem', _event.target.value)}
									>
										<MenuItem value="" disabled>Select...</MenuItem>
										{
											_formik.values.partGroup !== '' && (
												props.partGroups.find(
													(_partGroup) => _partGroup['Parts Group ID'] === _formik.values.partGroup
												)?.['Parts Inventory'].map(
													(_partGroupItem, _i) => (
														<MenuItem key={_i} value={_partGroupItem['Parts Inventory ID']}>{_partGroupItem['Parts Inventory Name']}</MenuItem>
													)
												)
											)
										}
									</TextField>
								</Grid>
								<Grid item xs={12}>
									<TextField
										name="name"
										value={_formik.values.name}
										label="Equipment"
										InputLabelProps={{
											shrink: true
										}}
										variant="outlined"
										margin="dense"
										fullWidth
										onChange={(_event) => _formik.setFieldValue('name', _event.target.value)}
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										name="partNumber"
										value={_formik.values.partNumber}
										label="Equipment No."
										InputLabelProps={{
											shrink: true
										}}
										variant="outlined"
										margin="dense"
										fullWidth
										onChange={(_event) => _formik.setFieldValue('partNumber', _event.target.value)}
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										name="serialNumber"
										value={_formik.values.serialNumber}
										label="Serial"
										InputLabelProps={{
											shrink: true
										}}
										variant="outlined"
										margin="dense"
										fullWidth
										onChange={(_event) => _formik.setFieldValue('serialNumber', _event.target.value)}
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<TextField
										name="cost"
										value={_formik.values.cost}
										type="number"
										label="Cost"
										InputLabelProps={{
											shrink: true
										}}
										variant="outlined"
										margin="dense"
										fullWidth
										onChange={(_event) => _formik.setFieldValue('cost', _event.target.value)}
									/>
									{
										_formik.errors.cost !== undefined && (
											<p className="error">{_formik.errors.cost}</p>
										)
									}
								</Grid>
								<Grid item xs={12} md={6}>
									<TextField
										name="currency"
										value={_formik.values.currency}
										label="Currency"
										select
										InputLabelProps={{
											shrink: true
										}}
										variant="outlined"
										margin="dense"
										fullWidth
										onChange={(_event) => _formik.setFieldValue('currency', _event.target.value)}
									>
										<MenuItem value="" disabled>Select...</MenuItem>
										{
											Currencies.map(
												(_currency, _i) => (
													<MenuItem key={_i} value={_currency.id}>{_currency.code}</MenuItem>
												)
											)
										}
									</TextField>
								</Grid>
								<Grid item xs={12} md={6}>
									<TextField
										name="dateInstalled"
										value={_formik.values.dateInstalled}
										type="date"
										label="Install Date"
										InputLabelProps={{
											shrink: true
										}}
										variant="outlined"
										margin="dense"
										fullWidth
										onChange={(_event) => _formik.setFieldValue('dateInstalled', _event.target.value)}
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<TextField
										name="dateServiced"
										value={_formik.values.dateServiced}
										type="date"
										label="Last Service Date"
										InputLabelProps={{
											shrink: true
										}}
										variant="outlined"
										margin="dense"
										fullWidth
										onChange={(_event) => _formik.setFieldValue('dateServiced', _event.target.value)}
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<TextField
										name="serviceInterval"
										value={_formik.values.serviceInterval}
										type="number"
										label="Service Interval"
										InputLabelProps={{
											shrink: true
										}}
										variant="outlined"
										margin="dense"
										fullWidth
										onChange={(_event) => _formik.setFieldValue('serviceInterval', _event.target.value)}
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<TextField
										name="sortOrder"
										value={_formik.values.sortOrder}
										type="number"
										label="Sort Order"
										InputLabelProps={{
											shrink: true
										}}
										variant="outlined"
										margin="dense"
										fullWidth
										onChange={(_event) => _formik.setFieldValue('sortOrder', _event.target.value)}
									/>
									{
										_formik.errors.sortOrder !== undefined && (
											<p className="error">{_formik.errors.sortOrder}</p>
										)
									}
								</Grid>
								<Grid item xs={12} md={6}>
									<TextField
										name="active"
										value={_formik.values.active}
										label="Active"
										select
										InputLabelProps={{
											shrink: true
										}}
										variant="outlined"
										margin="dense"
										fullWidth
										onChange={(_event) => _formik.setFieldValue('active', _event.target.value)}
									>
										<MenuItem value={1}>Yes</MenuItem>
										<MenuItem value={0}>No</MenuItem>
									</TextField>
								</Grid>
							</Grid>
						</DialogContent>
						<DialogActions>
							<Button variant="contained" color="default" disabled={_formik.isSubmitting} onClick={props.onClose}>Cancel</Button>
							<Button variant="contained" color="primary" disabled={_formik.isSubmitting} onClick={() => _formik.handleSubmit()}>Save</Button>
						</DialogActions>
					</Dialog>
				)
			}
		</Formik>
	);
};